import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { 
  Shield, Users, FileWarning, Heart, LogOut, 
  TrendingUp, Check, X, Eye, Clock, IdCard, Bot
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";

const AdminVerificationsPage = () => {
  const navigate = useNavigate();
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [stats, setStats] = useState(null);

  const adminToken = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [adminToken, filter]);

  const fetchData = async () => {
    try {
      const [verificationsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/verifications?status=${filter}`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        })
      ]);
      setVerifications(verificationsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Error fetching verification data");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (verificationId, action, reason = null) => {
    try {
      await axios.post(
        `${API}/admin/verifications/${verificationId}/action`,
        { action, reason },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(`Verification ${action}d successfully`);
      setSelectedVerification(null);
      fetchData();
    } catch (error) {
      toast.error(`Error: ${error.response?.data?.detail || "Failed to process"}`);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const getPhotoUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-rose-500 font-serif text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-800 border-r border-slate-700 p-6 hidden lg:block">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="font-serif text-lg font-bold text-white block">SingleSeen</span>
            <span className="text-rose-400 text-xs">Admin Panel</span>
          </div>
        </div>

        <nav className="space-y-2">
          <Link 
            to="/admin/dashboard"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          >
            <TrendingUp className="w-5 h-5" />
            Dashboard
          </Link>
          <Link 
            to="/admin/verifications"
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-rose-500/10 text-rose-400 font-medium"
          >
            <IdCard className="w-5 h-5" />
            ID Verification
            {stats?.id_verifications?.pending > 0 && (
              <span className="ml-auto bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                {stats.id_verifications.pending}
              </span>
            )}
          </Link>
          <Link 
            to="/admin/reports"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          >
            <FileWarning className="w-5 h-5" />
            Reports
            {stats?.reports?.pending > 0 && (
              <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {stats.reports.pending}
              </span>
            )}
          </Link>
          <Link 
            to="/admin/users"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          >
            <Users className="w-5 h-5" />
            Users
          </Link>
          <Link 
            to="/admin/stories"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          >
            <Heart className="w-5 h-5" />
            Love Stories
          </Link>
          <Link 
            to="/admin/seed-profiles"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          >
            <Bot className="w-5 h-5" />
            Seed Profiles
          </Link>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-slate-600 text-slate-400 hover:bg-slate-700 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-4 sm:p-8">
        <div className="mb-8">
          <h1 className="font-serif text-3xl text-white mb-2">ID Verification</h1>
          <p className="text-slate-400">Review and verify user passport documents</p>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {["pending", "approved", "rejected"].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg capitalize transition-colors ${
                filter === status
                  ? "bg-rose-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:bg-slate-700"
              }`}
            >
              {status}
            </button>
          ))}
        </div>

        {/* Verifications List */}
        {verifications.length === 0 ? (
          <div className="bg-slate-800 rounded-2xl p-12 border border-slate-700 text-center">
            <IdCard className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">No {filter} verification requests</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {verifications.map((verification) => (
              <div
                key={verification.id}
                className="bg-slate-800 rounded-2xl p-6 border border-slate-700"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    {/* Profile Photo */}
                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-700">
                      {verification.profile_photo_url ? (
                        <img
                          src={getPhotoUrl(verification.profile_photo_url)}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-500">
                          <Users className="w-8 h-8" />
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-white font-medium text-lg">{verification.user_name}</h3>
                      <p className="text-slate-400 text-sm">{verification.user_email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-500 text-sm">
                          {new Date(verification.submitted_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => setSelectedVerification(verification)}
                      className="bg-slate-700 text-white hover:bg-slate-600"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Review
                    </Button>
                    {filter === "pending" && (
                      <>
                        <Button
                          onClick={() => handleAction(verification.id, "approve")}
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => {
                            const reason = prompt("Rejection reason (optional):");
                            handleAction(verification.id, "reject", reason);
                          }}
                          variant="outline"
                          className="border-red-500 text-red-400 hover:bg-red-500/10"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Status Badge */}
                {verification.status !== "pending" && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                      verification.status === "approved"
                        ? "bg-emerald-500/20 text-emerald-400"
                        : "bg-red-500/20 text-red-400"
                    }`}>
                      {verification.status === "approved" ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      {verification.status.charAt(0).toUpperCase() + verification.status.slice(1)}
                    </span>
                    {verification.review_reason && (
                      <p className="text-slate-400 text-sm mt-2">Reason: {verification.review_reason}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Review Modal */}
      {selectedVerification && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-2xl text-white">
                  Verification Review: {selectedVerification.user_name}
                </h2>
                <button
                  onClick={() => setSelectedVerification(null)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <p className="text-slate-400 mb-6">
                Compare the passport photo with the user's profile photo to verify identity.
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Passport Document */}
                <div className="space-y-3">
                  <h3 className="text-white font-medium flex items-center gap-2">
                    <IdCard className="w-5 h-5 text-rose-400" />
                    Passport Document
                  </h3>
                  <div className="aspect-[4/3] bg-slate-900 rounded-xl overflow-hidden border border-slate-700">
                    {selectedVerification.passport_url ? (
                      <img
                        src={getPhotoUrl(selectedVerification.passport_url)}
                        alt="Passport"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-500">
                        No passport uploaded
                      </div>
                    )}
                  </div>
                </div>

                {/* Profile Photo */}
                <div className="space-y-3">
                  <h3 className="text-white font-medium flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    Profile Photo
                  </h3>
                  <div className="aspect-[4/3] bg-slate-900 rounded-xl overflow-hidden border border-slate-700">
                    {selectedVerification.profile_photo_url ? (
                      <img
                        src={getPhotoUrl(selectedVerification.profile_photo_url)}
                        alt="Profile"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-500">
                        No profile photo
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* All Profile Photos */}
              {selectedVerification.user_photos && selectedVerification.user_photos.length > 1 && (
                <div className="mb-6">
                  <h3 className="text-white font-medium mb-3">All Profile Photos</h3>
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {selectedVerification.user_photos.map((photo, idx) => (
                      <img
                        key={idx}
                        src={getPhotoUrl(photo)}
                        alt={`Photo ${idx + 1}`}
                        className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {selectedVerification.status === "pending" && (
                <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                  <Button
                    onClick={() => setSelectedVerification(null)}
                    variant="outline"
                    className="border-slate-600 text-slate-400"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      const reason = prompt("Rejection reason (optional):");
                      handleAction(selectedVerification.id, "reject", reason);
                    }}
                    variant="outline"
                    className="border-red-500 text-red-400 hover:bg-red-500/10"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleAction(selectedVerification.id, "approve")}
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Approve Verification
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVerificationsPage;
